import { Subtitle1 } from '@backoffice-frontend/patterns'
import { VideoCameraSlashIcon, WarningIcon } from '@moia-dev/pace-icons'
import { Button, Tag } from '@moia-dev/pace-web'
import { useAudioVideo } from 'amazon-chime-sdk-component-library-react'
import { useEffect, useImperativeHandle, useRef } from 'react'
import { useTranslation } from 'react-i18next'

type VideoTileProps = {
  tileId: number
  name?: string
  ref?: React.Ref<HTMLVideoElement>
}

export const VideoTile = ({ tileId, name, ref }: VideoTileProps) => {
  const audioVideo = useAudioVideo()
  const localRef = useRef<HTMLVideoElement>(null)

  useImperativeHandle(ref, () => localRef.current!, [])

  useEffect(() => {
    const video = localRef.current
    if (!video) {
      return
    }

    if (!audioVideo) {
      return
    }

    audioVideo.bindVideoElement(tileId, video)

    return () => {
      const tile = audioVideo.getVideoTile(tileId)
      if (tile) {
        audioVideo.unbindVideoElement(tileId)
      }
    }
  }, [audioVideo, tileId])

  return (
    <div
      css={{
        display: 'flex',
        position: 'relative',
        overflow: 'hidden',
        height: '100%',
        width: '100%',
        '&:hover > .name': {
          display: 'block',
        },
      }}
    >
      {name && (
        <div
          css={theme => ({
            display: 'none',
            position: 'absolute',
            bottom: theme.spacing(1),
            left: theme.spacing(1),
          })}
          className="name"
        >
          <Tag label={name} id={name} />
        </div>
      )}
      <video
        ref={localRef}
        css={{ height: '100%', width: '100%', borderRadius: 8 }}
        autoPlay
        playsInline
        muted
      />
    </div>
  )
}

type PreviewTileProps = {
  name: string
  isFocused: boolean
  ref?: React.Ref<HTMLCanvasElement>
}

export const PreviewTile = ({
  name,
  isFocused = false,
  ref,
}: PreviewTileProps) => {
  return (
    <div
      css={{
        display: 'flex',
        position: 'relative',
        overflow: 'hidden',
        height: '100%',
        width: '100%',
        '&:hover > .name': {
          display: 'block',
        },
      }}
    >
      {isFocused && (
        <div
          css={theme => ({
            position: 'absolute',
            inset: '0 0',
            borderRadius: 8,
            border: `4px solid ${theme.semantic.ColorContentDefault}`,
          })}
        />
      )}
      <div
        css={theme => ({
          position: 'absolute',
          bottom: theme.spacing(1),
          left: theme.spacing(1),
        })}
        className="name"
      >
        <Tag label={name} id={name} />
      </div>
      <canvas ref={ref} css={{ width: '100%', height: '100%' }} />
    </div>
  )
}

export const NoVideoTile = ({ onRetry }: { onRetry?: () => void }) => {
  const { t } = useTranslation()
  return (
    <div
      css={theme => ({
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.space.Space2,
        maxHeight: 300,
        padding: theme.spacing(2),
      })}
    >
      <VideoCameraSlashIcon
        css={theme => ({
          width: 120,
          height: 120,
          color: theme.semantic.ColorContentReduced,
        })}
      />
      <div
        css={{
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <WarningIcon
          css={theme => ({
            width: 20,
            height: 20,
            marginRight: theme.spacing(1),
            color: theme.semantic.ColorContentReduced,
          })}
        />
        <Subtitle1
          css={theme => ({ color: theme.semantic.ColorContentReduced })}
        >
          {t('adFleetMap_videoStreamErrorNotAvailable_caption')}
        </Subtitle1>
      </div>
      {onRetry && (
        <div css={theme => ({ marginTop: theme.space.Space8 })}>
          <Button
            variant="primary"
            label={t('adFleetMap_videoStreamRetry_button')}
            onClick={onRetry}
          />
        </div>
      )}
    </div>
  )
}
